// ============================================================================
//  ClaimTemplateApply.js — put a condition template onto the claim being edited
// ----------------------------------------------------------------------------
//  "We open a claim to edit. We have the privilege to set a template and apply that
//  template to the claim we have selected. It should override everything."
//
//  ── IT IS FOLDED AWAY UNTIL ASKED FOR ───────────────────────────────────────
//  Overriding a claim is not what happens on most claims, and a permanently open
//  panel offering to delete four sections is the kind of control people click by
//  accident. One button opens it.
//
//  ── THE OFFICER NEVER PRESSES APPLY WITHOUT HAVING SEEN THE ANSWER ──────────
//  Picking a template asks the server, which judges THIS claim against THIS
//  patient — the real date of birth, gender and attendance — and reports back
//  either the refusal or the exact rows that would go and arrive. Only then does
//  the button become live. The server re-judges on apply regardless, so a stale
//  screen cannot write a wrong row; this is so the officer is not surprised.
//
//  ── REPLACE IS THE DEFAULT, AND IT SAYS WHAT IT MEANS ───────────────────────
//  It is what was asked for. It is also destructive, so the rows it would remove
//  are listed by name and code before anything is pressed — not counted. "4
//  entries" is not something anybody can check their own work against.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Layers, Search, Check, Ban, AlertTriangle, Loader2, ChevronDown, ChevronUp,
  Trash2, Plus, X,
} from 'lucide-react';

import { conditionTemplates, templateApplyCheck, templateApply } from '../BaseURL';

const C = {
  ink: '#0F172A',
  inkSoft: '#334155',
  inkMuted: '#64748B',
  inkFaint: '#94A3B8',
  line: '#E2E8F0',
  head: '#F1F5F9',
  green: '#0F5132',
  greenSoft: '#E7F3EC',
  danger: '#A62828',
  dangerSoft: '#FBE9E9',
  dangerLine: '#F0C4C4',
  caution: '#8A5510',
  cautionSoft: '#FDF2E3',
  cautionLine: '#F2DCB8',
};

const MONO = {
  fontFamily: 'ui-monospace, "Cascadia Mono", Consolas, monospace',
  fontVariantNumeric: 'tabular-nums',
};

const SECTION_LABEL = {
  procedure: 'Procedure',
  diagnosis: 'Diagnosis',
  investigation: 'Lab / scan',
  medication: 'Medication',
};

//  The two modes, said as an outcome rather than as a verb. "Replace" is a thing
//  that happens to the claim; "override" is jargon for the same thing.
const MODES = [
  { key: 'replace', label: 'Replace what is there',
    hint: 'Removes the claim\'s current entries and writes the template\'s instead.' },
  { key: 'add', label: 'Add to what is there',
    hint: 'Keeps the claim\'s entries and adds anything the template has that it does not.' },
];

//  ── THE CLAIM'S MODEL TRAVELS WITH ITS ID ──────────────────────────────────
//
//  The endpoint used to assume every id was an imported claim, so a template
//  could not be applied to a typed-in one — and where the two models shared a
//  number it would have rewritten a DIFFERENT patient's claim. It takes
//  `source:id` now. `source` defaults to imported so a caller that has not been
//  taught about the distinction behaves exactly as before.
const refOf = (claimId, source) => `${source || 'imported'}:${claimId}`;

export default function ClaimTemplateApply({ claimId, source, onApplied }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [listErr, setListErr] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [mode, setMode] = useState('replace');
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  //  Loaded when the panel is first opened, not on mount: this sits on a claim
  //  editor that opens on every claim, and a library fetch per claim for a panel
  //  nobody opened is a request per row of the month.
  useEffect(() => {
    if (!open || list !== null) return;
    let alive = true;
    axios.get(conditionTemplates)
      .then(({ data }) => { if (alive) setList(data?.templates || []); })
      .catch((e) => {
        if (!alive) return;
        setList([]);
        setListErr(e?.response?.data?.error || e.message);
      });
    return () => { alive = false; };
  }, [open, list]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list || [];
    return (list || []).filter((t) => [
      t.name,
      ...(t.conditions || []).map((c) => `${c.icd10_code} ${c.name}`),
    ].join(' ').toLowerCase().includes(needle));
  }, [list, q]);

  //  ── ASK THE SERVER ────────────────────────────────────────────────────────
  //  On every change of template or mode, because the answer depends on both. The
  //  screen never decides for itself whether a template fits — a form holding a
  //  second opinion about NHIA's age rules is a form that will eventually disagree
  //  with the one that matters.
  const ask = useCallback(async (templateId, useMode) => {
    if (!templateId) { setCheck(null); return; }
    setBusy(true);
    setErr(null);
    setDone(null);
    try {
      const { data } = await axios.post(templateApplyCheck, {
        templateId, claimIds: [refOf(claimId, source)], mode: useMode,
      });
      setCheck(data);
    } catch (e) {
      setCheck(null);
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
    //  `source` is a dependency: the same id on the two models is two different
    //  claims, so a stale closure here would judge the wrong one.
  }, [claimId, source]);

  useEffect(() => { ask(picked, mode); }, [picked, mode, ask]);

  //  This claim's own verdict. The endpoint answers for a list because Multi Vet
  //  uses the same one; here the list is always one long.
  const mine = check?.claims?.[0] || null;
  const refusals = mine?.refusals || [];
  const warnings = mine?.warnings || [];
  const canApply = !!mine && mine.verdict === 'ok' && !busy;

  const apply = async () => {
    if (!canApply) return;
    setBusy(true);
    setErr(null);
    try {
      const { data } = await axios.post(templateApply, {
        templateId: picked, claimIds: [refOf(claimId, source)], mode, dryRun: false,
      });
      setDone(data);
      //  The claim has changed underneath the editor — a new diagnosis can change
      //  the resolved principal, and the row behind this panel shows that service
      //  code. The parent re-reads rather than this panel guessing.
      if (onApplied) onApplied();
      //  The check is now about a claim that no longer looks like this. Re-asked
      //  rather than left showing a plan that has already happened.
      await ask(picked, mode);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const chosen = (list || []).find((t) => t.id === picked) || null;

  // ── Folded ────────────────────────────────────────────────────────────────
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, font: 'inherit',
          fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 8,
          border: `1px solid ${C.line}`, background: '#fff', color: C.inkSoft,
          cursor: 'pointer',
        }}>
        <Layers size={14} color={C.green} />
        Apply a condition template
        <ChevronDown size={13} color={C.inkFaint} />
      </button>
    );
  }

  return (
    <div style={{
      border: `1px solid ${C.line}`, borderRadius: 11, background: '#fff',
      overflow: 'hidden', minWidth: 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '10px 13px',
        borderBottom: `1px solid ${C.line}`, background: C.head,
      }}>
        <Layers size={15} color={C.green} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 800, color: C.ink }}>
          Apply a condition template
        </span>
        <button type="button" onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            border: 0, background: 'none', cursor: 'pointer', color: C.inkMuted,
            padding: 2, lineHeight: 1, display: 'grid', placeItems: 'center',
          }}>
          <ChevronUp size={15} />
        </button>
      </div>

      <div style={{ padding: '11px 13px', display: 'grid', gap: 12, minWidth: 0 }}>
        {/* ── 1. WHICH TEMPLATE ──────────────────────────────────────────── */}
        <div>
          <Label>1. Which template</Label>
          {listErr && <Bad>{listErr}</Bad>}
          {list === null ? (
            <Muted><Loader2 size={12} className="mf-spin" /> Loading the library…</Muted>
          ) : list.length === 0 ? (
            <Muted>
              No condition templates yet. Build one under
              {' '}<b>Claims ▸ Condition Templates</b> and it will appear here.
            </Muted>
          ) : (
            <>
              <div style={{ position: 'relative', marginBottom: 7 }}>
                <Search size={13} style={{
                  position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                  color: C.inkFaint, pointerEvents: 'none',
                }} />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or condition…"
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 28px',
                    borderRadius: 8, font: 'inherit', fontSize: 12.5, background: '#fff',
                    border: `1px solid ${C.line}`, color: C.ink,
                  }} />
              </div>
              <div style={{ display: 'grid', gap: 4, maxHeight: 190, overflowY: 'auto' }}>
                {shown.length === 0 ? (
                  <Muted>Nothing matches “{q.trim()}”.</Muted>
                ) : shown.map((t) => {
                  const on = t.id === picked;
                  return (
                    <button key={t.id} type="button"
                      onClick={() => setPicked(on ? null : t.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                        font: 'inherit', padding: '7px 9px', borderRadius: 8, minWidth: 0,
                        cursor: 'pointer',
                        border: `1px solid ${on ? C.green : C.line}`,
                        background: on ? C.greenSoft : '#fff',
                      }}>
                      <span style={{
                        flexShrink: 0, width: 16, height: 16, borderRadius: 4,
                        display: 'grid', placeItems: 'center',
                        border: `1px solid ${on ? C.green : C.line}`,
                        background: on ? C.green : '#fff', color: '#fff',
                      }}>{on ? <Check size={10} /> : null}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                          display: 'block', fontSize: 12, fontWeight: on ? 800 : 650,
                          color: on ? C.green : C.ink,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{t.name}</span>
                        <span style={{
                          display: 'block', fontSize: 10.5, color: C.inkFaint,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {(t.conditions || []).map((c) => c.name).join(' + ') || '—'}
                          {' · '}{(t.items || []).length} to give
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── 2. HOW ─────────────────────────────────────────────────────── */}
        {picked && (
          <div>
            <Label>2. How</Label>
            <div style={{ display: 'grid', gap: 5 }}>
              {MODES.map((m) => {
                const on = m.key === mode;
                return (
                  <button key={m.key} type="button" onClick={() => setMode(m.key)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left',
                      font: 'inherit', padding: '8px 10px', borderRadius: 8, minWidth: 0,
                      cursor: 'pointer',
                      border: `1px solid ${on ? C.green : C.line}`,
                      background: on ? C.greenSoft : '#fff',
                    }}>
                    <span style={{
                      flexShrink: 0, width: 14, height: 14, borderRadius: 999, marginTop: 2,
                      border: `4px solid ${on ? C.green : C.line}`, background: '#fff',
                    }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 12, fontWeight: on ? 800 : 700,
                        color: on ? C.green : C.inkSoft,
                      }}>{m.label}</span>
                      <span style={{
                        display: 'block', fontSize: 11, color: C.inkMuted, marginTop: 1,
                        lineHeight: 1.45,
                      }}>{m.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── 3. WHAT THE SERVER SAYS ────────────────────────────────────── */}
        {picked && (
          <div>
            <Label>3. Whether it fits this patient</Label>

            {busy && !mine && (
              <Muted><Loader2 size={12} className="mf-spin" /> Checking…</Muted>
            )}
            {err && <Bad>{err}</Bad>}

            {mine && (
              <>
                {/*  WHO THIS CLAIM IS, read back. The refusals below are about this
                     person, and an officer who cannot see the age the server used
                     cannot tell a correct refusal from a data problem. */}
                <div style={{
                  display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8,
                }}>
                  {[
                    mine.facts?.age == null ? 'age not known' : `${mine.facts.age} years`,
                    mine.facts?.band || 'band not known',
                    mine.facts?.sex || 'gender not recorded',
                    mine.facts?.setting === 'inpatient' ? 'in-patient'
                      : mine.facts?.setting === 'outpatient' ? 'out-patient'
                        : 'attendance not recorded',
                  ].map((chip, i) => (
                    <span key={i} style={{
                      padding: '3px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                      background: C.head, color: C.inkMuted, border: `1px solid ${C.line}`,
                    }}>{chip}</span>
                  ))}
                </div>

                {/*  REFUSALS. Each one is a sentence naming the patient, the tariff
                     and the reason — there is no correct version of this write, so
                     there is no "apply anyway". */}
                {refusals.length > 0 && (
                  <div style={{
                    border: `1px solid ${C.dangerLine}`, background: C.dangerSoft,
                    borderRadius: 9, padding: '9px 11px', display: 'grid', gap: 6,
                  }}>
                    {refusals.map((r, i) => (
                      <div key={i} style={{
                        display: 'flex', gap: 7, fontSize: 11.5, color: C.danger,
                        lineHeight: 1.5,
                      }}>
                        <Ban size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{r.why}</span>
                      </div>
                    ))}
                  </div>
                )}

                {warnings.length > 0 && (
                  <div style={{
                    border: `1px solid ${C.cautionLine}`, background: C.cautionSoft,
                    borderRadius: 9, padding: '9px 11px', display: 'grid', gap: 6,
                    marginTop: refusals.length ? 7 : 0,
                  }}>
                    {warnings.map((w, i) => (
                      <div key={i} style={{
                        display: 'flex', gap: 7, fontSize: 11.5, color: C.caution,
                        lineHeight: 1.5,
                      }}>
                        <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{w.why}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/*  ── WHAT WOULD CHANGE ────────────────────────────────────
                     Only when it can go on. Listing the damage beside a refusal
                     would offer a choice that does not exist.

                     The rows that go are named, not counted: this is the part the
                     officer is authorising, and it cannot be undone from here. */}
                {mine.verdict === 'ok' && (
                  <div style={{ marginTop: refusals.length || warnings.length ? 8 : 0 }}>
                    {mine.plan.remove.length > 0 && (
                      <Change tone="out" icon={Trash2}
                        title={`${mine.plan.remove.length} entr${mine.plan.remove.length === 1 ? 'y' : 'ies'} removed`}
                        rows={mine.plan.remove} />
                    )}
                    <Change tone="in" icon={Plus}
                      title={`${mine.plan.adds} to be written`}
                      rows={mine.plan.add.filter((a) => !a.already)} />
                    {mine.plan.alreadyThere > 0 && (
                      <Muted>
                        {mine.plan.alreadyThere} already on this claim — left as they are.
                      </Muted>
                    )}
                    {mine.plan.remove.length === 0 && mode === 'replace' && (
                      <Muted>This claim has nothing recorded yet, so nothing is removed.</Muted>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── DONE ───────────────────────────────────────────────────────── */}
        {done && (() => {
          const r = done.claims?.[0];
          //  A FAILURE IS NOT A GREEN BOX. The first version always reported success
          //  and printed the counts, so a claim whose write threw said "was applied —
          //  0 removed, 0 written" in green. The server returns the reason per claim;
          //  it just was not being read.
          const bad = !!(r && (r.error || r.applied === false));
          return (
            <div style={{
              border: `1px solid ${bad ? C.dangerLine : `${C.green}44`}`,
              background: bad ? C.dangerSoft : C.greenSoft,
              borderRadius: 9, padding: '9px 11px', fontSize: 11.5,
              color: bad ? C.danger : C.green, lineHeight: 1.55,
            }}>
              {bad ? (
                <>
                  <b>{chosen ? chosen.name : 'The template'}</b> could not be applied
                  {(r.added || r.removed)
                    ? <> — and this claim was <b>part written</b>: {r.added || 0} added,
                        {' '}{r.removed || 0} removed before it failed. Check the sections
                        below.</>
                    : '. Nothing on this claim changed.'}
                  {r.error && (
                    <div style={{ marginTop: 5 }}>{r.error}</div>
                  )}
                </>
              ) : (
                <>
                  <b>{chosen ? chosen.name : 'The template'}</b> was applied
                  {!r ? '.' : ` — ${r.added || 0} written, ${r.removed || 0} removed`
                    + `${r.skipped ? `, ${r.skipped} left as they were` : ''}.`}
                </>
              )}
            </div>
          );
        })()}

        {/* ── THE BUTTON ─────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap',
          borderTop: `1px solid ${C.line}`, paddingTop: 10,
        }}>
          <button type="button" onClick={apply} disabled={!canApply}
            //  The REASON it is disabled, on the control. A dead button with no
            //  explanation is where an officer stops and asks somebody.
            title={!picked ? 'Pick a template first'
              : busy ? 'Checking…'
                : refusals.length ? 'This claim cannot take this template — see above'
                  : undefined}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, font: 'inherit',
              fontSize: 12.5, fontWeight: 800, padding: '9px 15px', borderRadius: 8,
              border: '1px solid transparent', color: '#fff',
              cursor: canApply ? 'pointer' : 'not-allowed',
              background: canApply ? C.green : C.inkFaint,
            }}>
            {busy ? <Loader2 size={13} className="mf-spin" /> : <Check size={13} />}
            {mode === 'replace' ? 'Replace and apply' : 'Add to this claim'}
          </button>
          <button type="button" onClick={() => setOpen(false)}
            style={{
              font: 'inherit', fontSize: 12.5, fontWeight: 700, padding: '9px 14px',
              borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff',
              color: C.inkSoft, cursor: 'pointer',
            }}>
            Close
          </button>
          {/*  Said beside the button rather than as a tooltip on it, because this is
               the sentence that stops somebody pressing it by mistake. */}
          {canApply && mode === 'replace' && mine.plan.remove.length > 0 && (
            <span style={{ fontSize: 11, color: C.caution, fontWeight: 700 }}>
              This removes {mine.plan.remove.length} existing
              {' '}entr{mine.plan.remove.length === 1 ? 'y' : 'ies'}. It cannot be undone here.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Small pieces ────────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase',
    color: C.inkFaint, marginBottom: 6,
  }}>{children}</div>
);

const Muted = ({ children }) => (
  <div style={{
    fontSize: 11.5, color: C.inkMuted, lineHeight: 1.55, display: 'flex',
    alignItems: 'center', gap: 6, marginTop: 4,
  }}>{children}</div>
);

const Bad = ({ children }) => (
  <div style={{
    fontSize: 11.5, color: C.danger, lineHeight: 1.5, marginBottom: 6,
    display: 'flex', alignItems: 'flex-start', gap: 6,
  }}>
    <X size={13} style={{ flexShrink: 0, marginTop: 2 }} />
    <span>{children}</span>
  </div>
);

//  One side of the change. Grouped by section so a long list reads as four short
//  ones rather than one column of codes.
const Change = ({ tone, icon: Icon, title, rows }) => {
  if (!rows.length) return null;
  const out = tone === 'out';
  const bySection = rows.reduce((m, r) => {
    (m[r.section] = m[r.section] || []).push(r);
    return m;
  }, {});
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 800,
        color: out ? C.danger : C.green, marginBottom: 3,
      }}>
        <Icon size={12} />
        {title}
      </div>
      {Object.entries(bySection).map(([section, list]) => (
        <div key={section} style={{
          fontSize: 11, color: C.inkSoft, lineHeight: 1.6, paddingLeft: 18,
        }}>
          <span style={{ color: C.inkFaint }}>{SECTION_LABEL[section] || section}: </span>
          {list.map((r, i) => (
            <span key={`${r.code}-${i}`}>
              {i > 0 ? ', ' : ''}
              {r.name}
              <span style={{ ...MONO, color: C.inkFaint, fontSize: 10 }}> {r.code}</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};
